import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import axios from "axios";
import { ScrollReveal, Overline } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AVATAR_1 =
  "https://images.unsplash.com/photo-1762314907825-c6c591f94ea2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwzfHxzdHJlZXR3ZWFyJTIwc3BvcnRzJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODYzNTMzNjV8MA&ixlib=rb-4.1.0&q=85";
const AVATAR_2 =
  "https://images.unsplash.com/photo-1596129050968-24ea0d26f0ce?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NjV8MHwxfHNlYXJjaHwyfHxzdHJlZXR3ZWFyJTIwc3BvcnRzJTIwcG9ydHJhaXQlMjBkYXJrJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODYzNTMzNjV8MA&ixlib=rb-4.1.0&q=85";

const QUOTES = [
  {
    quote:
      "Honestly didn't expect much from a bit of fabric and glue. First run with it on I knocked 40 seconds off my 5k — could be placebo, but I'll take it. Stays put even when I'm dripping with sweat.",
    name: "Josh M.",
    role: "Recreational runner, VIC",
    avatar: AVATAR_1,
  },
  {
    quote:
      "Bought it for the gym, ended up wearing it to bed. My wife reckons the snoring's basically gone, which apparently makes it the best thirty bucks I've ever spent. Peels off clean in the morning too.",
    name: "Daniel R.",
    role: "Shift worker, QLD",
    avatar: AVATAR_2,
  },
  {
    quote:
      "Took two nights to stop noticing it on my nose. Now I wake up without the dry mouth and I'm not wrecked by 3pm anymore. Didn't realise how bad my sleep breathing was until it wasn't.",
    name: "Sarah T.",
    role: "New mum, NSW",
    avatar: null,
  },
];

const Stars = ({ rating }) => (
  <div className="flex gap-1 mb-6" aria-label={`${rating} out of 5 stars`}>
    {[1, 2, 3, 4, 5].map((s) => (
      <Star
        key={s}
        size={14}
        className={s <= rating ? "text-volt fill-volt" : "text-white/20"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const Testimonials = () => {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then(({ data }) => setReviews(data.reviews || []))
      .catch(() => {});
  }, []);

  return (
    <section
      id="stories"
      data-testid="testimonials-section"
      className="relative py-28 md:py-40 px-6 md:px-10 border-t border-white/10"
    >
      <div className="max-w-[1600px] mx-auto">
        <ScrollReveal>
          <Overline className="mb-4">Field Reports</Overline>
          <h2 className="font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
            Worn loud<span className="text-volt">.</span>
          </h2>
        </ScrollReveal>

        <div className="mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {QUOTES.map((t, i) => (
            <ScrollReveal key={t.name} delay={i * 0.12} className={i === 1 ? "md:mt-12" : ""}>
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className="group border border-white/10 bg-[#121212] p-8 h-full flex flex-col transition-colors duration-300 hover:border-volt/60"
              >
                {t.avatar ? (
                  <div className="w-16 h-16 overflow-hidden border border-white/15 mb-8">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-full h-full object-cover grayscale contrast-125 transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                ) : (
                  <div className="w-16 h-16 mb-8 bg-volt flex items-center justify-center">
                    <span className="font-anton text-black text-xl">{t.name[0]}</span>
                  </div>
                )}
                <blockquote className="text-lg md:text-xl font-light leading-snug text-white/90 flex-1">
                  "{t.quote}"
                </blockquote>
                <figcaption className="mt-8 pt-4 border-t border-white/10 font-plex text-xs uppercase tracking-[0.25em]">
                  <span className="text-white">{t.name}</span>
                  <span className="text-white/40"> — {t.role}</span>
                </figcaption>
              </figure>
            </ScrollReveal>
          ))}
          {reviews.map((r, i) => (
            <ScrollReveal key={r.id} delay={i * 0.12}>
              <figure
                data-testid={`review-card-${i + 1}`}
                className="group border border-volt/30 bg-[#121212] p-8 h-full flex flex-col transition-colors duration-300 hover:border-volt/60"
              >
                <Stars rating={r.rating} />
                <blockquote className="text-lg md:text-xl font-light leading-snug text-white/90 flex-1">
                  "{r.text}"
                </blockquote>
                <figcaption className="mt-8 pt-4 border-t border-white/10 font-plex text-xs uppercase tracking-[0.25em]">
                  <span className="text-white">{r.name}</span>
                  <span className="text-volt/80"> — Verified buyer</span>
                </figcaption>
              </figure>
            </ScrollReveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
